import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { User } from "firebase/auth";
import React from "react";
import { useApprovalStatus } from "../hooks/useApprovalStatus";
import { signOutUser } from "../services/firebase";
import { createLogger } from "../utils/logger";
import { PendingApprovalScreen } from "./PendingApprovalScreen";

const logger = createLogger("ApprovalGuard");

interface ApprovalGuardProps {
  user: User | null;
  children: React.ReactNode;
}

export const ApprovalGuard: React.FC<ApprovalGuardProps> = ({ user, children }) => {
  const { approvalStatus, isLoading, error, isApproved, refetch, shouldLogout } = useApprovalStatus(user);

  // Check if user's email is not verified
  if (user && !user.emailVerified) {
    const handleSignOut = async () => {
      try {
        await signOutUser();
        logger.info("User signed out from email verification screen");
      } catch (error) {
        logger.error("Error signing out:", error);
      }
    };

    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
          p: 2,
        }}
      >
        <Typography variant="h5" gutterBottom color="primary">
          Verify Your Email Address
        </Typography>

        <Typography variant="body1" paragraph textAlign="center">
          We&apos;ve sent a verification email to <strong>{user.email}</strong>
        </Typography>

        <Typography variant="body2" color="text.secondary" paragraph textAlign="center">
          Please check your email and click the verification link to activate your account. You may need to check your spam folder.
        </Typography>

        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 2 }}>
          After verifying your email, please refresh the page to continue.
        </Typography>

        <Button variant="outlined" onClick={handleSignOut} sx={{ mt: 3 }}>
          Sign Out
        </Button>
      </Box>
    );
  }

  // Show loading state while checking approval status
  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Checking account status...
        </Typography>
      </Box>
    );
  }

  // Show error state if we can't check approval status
  if (error) {
    logger.error("Error checking approval status:", error);

    // If we should logout due to critical API failure, show a different message
    if (shouldLogout) {
      return (
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "background.default",
            p: 2,
          }}
        >
          <Typography variant="h6" color="error" gutterBottom>
            Session expired
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Unable to verify your account status. You have been logged out for security reasons.
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1 }}>
            Please sign in again to continue.
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
          p: 2,
        }}
      >
        <Typography variant="h6" color="error" gutterBottom>
          Unable to verify account status
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {error}
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1 }}>
          Please try refreshing the page or contact support if the problem persists.
        </Typography>
      </Box>
    );
  }

  // If user is not approved (pending or rejected), show approval screen
  if (user && approvalStatus && !isApproved) {
    return <PendingApprovalScreen approvalStatus={approvalStatus} onRefetch={refetch} />;
  }

  // If user is approved or no user, show the main app
  return <>{children}</>;
};
